import Phaser from 'phaser';
import { formatVeterancy, type Tower } from '../entities/Tower';
import { SYMBIOSIS } from '../data/symbiosis';
import { BATTLE_W, GAME_HEIGHT, UI_BOTTOM_H, UI_TOP_H, scaleSupplyCost, scaleUpgradeCost } from '../config/constants';
import { audio } from '../core/AudioSynth';
import { researchManager } from '../core/ResearchManager';

export interface TowerMenuCallbacks {
  onUpgrade: (t: Tower) => void;
  onPriority: (t: Tower) => void;
  onRepair: (t: Tower) => void;
  onEvolve: (t: Tower, toId: string) => void;
  onSymbiosis: (t: Tower, moduleId: string) => void;
  onBuyDrone?: (t: Tower) => void;
  onSell: (t: Tower) => void;
  onClose: () => void;
  getSupply: () => number;
  getDroneCount?: (t: Tower) => number;
  getDroneCost?: (t: Tower) => number | null;
  getRepairCost?: (t: Tower) => number | null;
}

type ButtonKind = 'upgrade' | 'priority' | 'repair' | 'evolve' | 'sym' | 'drone' | 'sell';

interface MenuButton {
  rect: Phaser.GameObjects.Rectangle;
  txt: Phaser.GameObjects.Text;
  enabled: boolean;
  active: boolean;
  kind: ButtonKind;
  ref?: string;
  color: string;
  locked?: boolean;
}

const W = 286;
const PAD = 12;

/**
 * Floating tooltip menu shown when a tower is clicked. Handles upgrading,
 * targeting priority, weapon evolution and symbiosis bolt-ons.
 *
 * The panel refreshes live (see `updateLive`) so costs, stats and
 * affordability track the battle without needing to reopen it.
 */
export class TowerMenu {
  private scene: Phaser.Scene;
  private cb: TowerMenuCallbacks;
  private container: Phaser.GameObjects.Container;
  private towerRef: Tower | null = null;
  private bounds = { x: 0, y: 0, w: W, h: 0 };

  private buttons: MenuButton[] = [];
  private dynStat?: Phaser.GameObjects.Text;
  private dynKills?: Phaser.GameObjects.Text;
  private liveStage = -1;
  private liveMax = false;
  private liveSecondary = '';
  private liveSig = '';

  constructor(scene: Phaser.Scene, cb: TowerMenuCallbacks) {
    this.scene = scene;
    this.cb = cb;
    this.container = scene.add.container(0, 0).setDepth(90).setVisible(false);
  }

  get isOpen(): boolean {
    return this.towerRef !== null;
  }

  get tower(): Tower | null {
    return this.towerRef;
  }

  contains(x: number, y: number): boolean {
    if (!this.towerRef) return false;
    const b = this.bounds;
    return x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;
  }

  close(): void {
    this.towerRef = null;
    this.container.setVisible(false);
    this.container.removeAll(true);
    this.buttons = [];
    this.dynStat = undefined;
    this.dynKills = undefined;
  }

  refresh(): void {
    if (this.towerRef) this.open(this.towerRef);
  }

  /**
   * Cheap per-frame refresh: updates stats, costs and enabled/disabled state
   * in place. Falls back to a full rebuild when the tower's structure changes
   * (upgrade, evolution, bolt-on) since that alters the layout.
   */
  updateLive(): void {
    const t = this.towerRef;
    if (!t) return;
    if (
      t.stageIndex !== this.liveStage ||
      t.isMax !== this.liveMax ||
      (t.secondary ?? '') !== this.liveSecondary
    ) {
      this.refresh();
      return;
    }

    const supply = this.cb.getSupply();
    const stage = t.stage;
    const sig = `${Math.floor(supply)}|${t.kills}|${Math.floor(t.damageDealt)}|${t.priority}|${t.stageIndex}`;
    if (sig === this.liveSig) return;
    this.liveSig = sig;
    this.dynStat?.setText(
      `DMG ${Math.round(t.damage)}   RNG ${Math.round(t.range)}   ROF ${stage.fireRate.toFixed(1)}/s`,
    );
    this.dynKills?.setText(`TYPE ${stage.damageType}   KILLS ${t.kills}   DMG ${Math.round(t.damageDealt)}   VET ${formatVeterancy(t.kills)}`);

    for (const b of this.buttons) {
      let enabled = b.enabled;
      switch (b.kind) {
        case 'upgrade': {
          const up = t.upgradeCost;
          enabled = up != null && supply >= up;
          b.txt.setText(up != null ? `UPGRADE   $${up}` : 'MAX STAGE');
          break;
        }
        case 'priority':
          b.txt.setText(`PRIORITY   ${t.priority}`);
          enabled = true;
          break;
        case 'repair': {
          const cost = this.cb.getRepairCost?.(t) ?? null;
          enabled = cost != null && t.hp < t.maxHp && supply >= cost;
          b.txt.setText(cost != null ? `REPAIR   +30 HP   $${cost}` : 'REPAIR   FULL');
          break;
        }
        case 'evolve': {
          const evo = t.def.evolvesTo?.find((e) => e.to === b.ref);
          const researched = !!evo && researchManager.isTowerResearchUnlocked(evo.to);
          b.locked = !researched;
          enabled = researched && supply >= scaleUpgradeCost(evo.cost);
          if (evo) {
            const evoCost = scaleUpgradeCost(evo.cost);
            b.txt.setText(researched ? `${evo.name}   $${evoCost}` : `${evo.name}   LOCKED · RESEARCH`);
          }
          break;
        }
        case 'sym': {
          const mod = b.ref ? SYMBIOSIS[b.ref] : undefined;
          const owned = t.secondary === b.ref;
          const licensed = !!b.ref && researchManager.isSymbiosisUnlocked(b.ref);
          const upgrade = owned ? t.secondaryUpgradeCost : null;
          const swapCost = !owned && b.ref ? t.secondarySwapCost(b.ref) : null;
          b.locked = !owned && !licensed;
          enabled = owned
            ? upgrade != null && supply >= upgrade
            : licensed && swapCost != null && supply >= swapCost;
          b.txt.setText(
            owned
              ? `* ${mod?.short ?? ''}  T${t.secondaryLevel}/3${upgrade != null ? `   $${upgrade}` : '  MAX'}`
              : licensed ? this.symbiosisLabel(t, b.ref ?? '', mod?.name ?? '') : `${mod?.name ?? ''}   LOCKED · RESEARCH`,
          );
          break;
        }
        case 'drone': {
          const count = this.cb.getDroneCount?.(t) ?? 0;
          const cost = this.cb.getDroneCost?.(t) ?? null;
          enabled = cost != null && supply >= cost;
          b.txt.setText(cost != null ? `ADD DRONE   ${count}/6   $${cost}` : `DRONE WING   ${count}/6`);
          break;
        }
        case 'sell':
          b.txt.setText(`SELL   $${t.sellValue}`);
          enabled = true;
          break;
      }
      b.enabled = enabled;
      this.applyButton(b);
    }
  }

  open(t: Tower): void {
    this.towerRef = t;
    this.container.removeAll(true);
    this.container.setVisible(true);
    this.buttons = [];
    this.dynStat = undefined;
    this.dynKills = undefined;
    this.liveStage = t.stageIndex;
    this.liveMax = t.isMax;
    this.liveSecondary = t.secondary ?? '';
    this.liveSig = '';

    const def = t.def;
    const stage = t.stage;
    const supply = this.cb.getSupply();
    const content: Phaser.GameObjects.GameObject[] = [];
    let y = PAD;

    // Header
    content.push(this.text(PAD, y, def.short, 15, '#e0e8f0', true));
    content.push(this.text(W - 30, y + 3, stage.name, 10, '#7a8fa0').setOrigin(1, 0));
    y += 21;
    content.push(this.text(PAD, y, def.name, 10, '#6a7a88'));
    y += 20;

    this.dynStat = this.text(PAD, y, `DMG ${Math.round(t.damage)}   RNG ${Math.round(t.range)}   ROF ${stage.fireRate.toFixed(1)}/s`, 11, '#9ad4e8');
    content.push(this.dynStat);
    y += 15;
    this.dynKills = this.text(PAD, y, `TYPE ${stage.damageType}   KILLS ${t.kills}   DMG ${Math.round(t.damageDealt)}   VET ${formatVeterancy(t.kills)}`, 11, '#9ad4e8');
    content.push(this.dynKills);
    y += 22;

    // Primary actions
    const upCost = t.upgradeCost;
    const canUp = upCost != null && supply >= upCost;
    content.push(
      this.button(PAD, y, W - PAD * 2, 24, upCost != null ? `UPGRADE   $${upCost}` : 'MAX STAGE', canUp, () => this.cb.onUpgrade(t), false, '#c8d4e0', 'upgrade'),
    );
    y += 29;
    content.push(
      this.button(PAD, y, W - PAD * 2, 24, `PRIORITY   ${t.priority}`, true, () => this.cb.onPriority(t), false, '#c8d4e0', 'priority'),
    );
    y += 29;

    const repairCost = this.cb.getRepairCost?.(t) ?? null;
    content.push(
      this.button(PAD, y, W - PAD * 2, 24, repairCost != null && t.hp < t.maxHp ? `REPAIR   +30 HP   $${repairCost}` : 'REPAIR   FULL', repairCost != null && t.hp < t.maxHp && supply >= repairCost, () => this.cb.onRepair(t), false, '#9ad4e8', 'repair'),
    );
    y += 29;

    // Evolution
    if (t.isMax && def.evolvesTo?.length) {
      content.push(this.text(PAD, y, 'EVOLVE — CHANGE WEAPON', 9, '#d4a24a'));
      y += 15;
      for (const evo of def.evolvesTo) {
      const evoCost = scaleUpgradeCost(evo.cost);
        const researched = researchManager.isTowerResearchUnlocked(evo.to);
        const can = researched && supply >= evoCost;
        content.push(
          this.button(
            PAD,
            y,
            W - PAD * 2,
            22,
            researched ? `${evo.name}   $${evoCost}` : `${evo.name}   LOCKED · RESEARCH`,
            can,
            () => this.cb.onEvolve(t, evo.to),
            false,
            researched ? '#c8d4e0' : '#ff6a6a',
            'evolve',
            evo.to,
            !researched,
          ),
        );
        y += 25;
      }
      y += 4;
    }

    // Symbiosis
    if (t.isMax && def.symbiosis?.length) {
        content.push(this.text(PAD, y, 'SYMBIOSIS — BOLT-ON / TUNE MODULE', 9, '#7dce5a'));
      y += 15;
      for (const mid of def.symbiosis) {
        const mod = SYMBIOSIS[mid];
        if (!mod) continue;
        const licensed = researchManager.isSymbiosisUnlocked(mid);
        const owned = t.secondary === mid;
        const upgrade = owned ? t.secondaryUpgradeCost : null;
        const swapCost = !owned ? t.secondarySwapCost(mid) : null;
        const can = owned ? upgrade != null && supply >= upgrade : licensed && swapCost != null && supply >= swapCost;
        const label = owned
          ? `* ${mod.short}  T${t.secondaryLevel}/3${upgrade != null ? `   $${upgrade}` : '  MAX'}`
          : licensed ? this.symbiosisLabel(t, mid, mod.name) : `${mod.name}   LOCKED · RESEARCH`;
        content.push(
          this.button(
            PAD,
            y,
            W - PAD * 2,
            22,
            label,
            can,
            () => this.cb.onSymbiosis(t, mid),
            owned,
            licensed ? '#c8d4e0' : '#ff6a6a',
            'sym',
            mid,
            !owned && !licensed,
          ),
        );
        y += 25;
      }
      y += 4;
    }

    if (t.def.id === 'dronehive' && this.cb.onBuyDrone) {
      const droneCount = this.cb.getDroneCount?.(t) ?? 3;
      const droneCost = this.cb.getDroneCost?.(t) ?? null;
      content.push(
        this.button(
          PAD,
          y,
          W - PAD * 2,
          22,
          droneCost != null ? `ADD DRONE   ${droneCount}/6   $${droneCost}` : `DRONE WING   ${droneCount}/6`,
          droneCost != null && supply >= droneCost,
          () => this.cb.onBuyDrone?.(t),
          false,
          '#9ad4e8',
          'drone',
        ),
      );
      y += 25;
    }

    content.push(
      this.button(PAD, y, W - PAD * 2, 22, `SELL   $${t.sellValue}`, true, () => this.cb.onSell(t), false, '#ff9a9a', 'sell'),
    );
    y += 27;

    const H = y + PAD;

    // Background
    const bg = this.scene.add.graphics();
    // Keep the battlefield visible through the action panel so the menu does
    // not hide the tower, target, or nearby route while it is open.
    bg.fillStyle(0x0a1016, 0.78);
    bg.fillRoundedRect(0, 0, W, H, 8);
    bg.lineStyle(1.5, def.colour, 0.9);
    bg.strokeRoundedRect(0, 0, W, H, 8);
    bg.fillStyle(def.colour, 0.95);
    bg.fillRect(0, 0, W, 3);
    bg.lineStyle(1, 0x1e2a34, 1);
    bg.lineBetween(PAD, 60, W - PAD, 60);
    this.container.add(bg);

    const close = this.scene.add
      .text(W - 14, 7, 'x', { fontFamily: 'Consolas, monospace', fontSize: '14px', color: '#8fa8b8' })
      .setOrigin(0.5, 0)
      .setInteractive({ useHandCursor: true });
    close.on('pointerover', () => close.setColor('#e0e8f0'));
    close.on('pointerout', () => close.setColor('#8fa8b8'));
    close.on('pointerdown', () => {
      audio.click();
      this.cb.onClose();
    });
    this.container.add(close);

    for (const c of content) this.container.add(c);

    // Anchor near the tower, clamped inside the battlefield.
    let bx = t.x + 26;
    if (bx + W > BATTLE_W - 8) bx = t.x - 26 - W;
    let by = t.y - H / 2;
    bx = Phaser.Math.Clamp(bx, 8, BATTLE_W - W - 8);
    by = Phaser.Math.Clamp(by, UI_TOP_H + 8, GAME_HEIGHT - UI_BOTTOM_H - H - 8);
    this.container.setPosition(bx, by);
    this.bounds = { x: bx, y: by, w: W, h: H };

    for (const b of this.buttons) this.applyButton(b);
  }

  private text(x: number, y: number, s: string, size: number, color: string, bold = false): Phaser.GameObjects.Text {
    return this.scene.add.text(x, y, s, {
      fontFamily: 'Consolas, monospace',
      fontSize: `${size}px`,
      color,
      fontStyle: bold ? 'bold' : 'normal',
    });
  }

  private symbiosisLabel(t: Tower, moduleId: string, moduleName: string): string {
    const cost = t.secondarySwapCost(moduleId);
    if (cost == null) return `${moduleName}   —`;
    if (!t.secondary) return `${moduleName}   $${cost}`;
    return cost >= 0
      ? `${moduleName}   SWAP $${cost}  75% CR`
      : `${moduleName}   REFUND +$${Math.abs(cost)}  75% CR`;
  }

  private applyButton(b: MenuButton): void {
    b.rect.setFillStyle(b.active ? 0x14301c : b.locked ? 0x301820 : 0x152028);
    b.rect.setStrokeStyle(1, b.active ? 0x7dce5a : b.locked ? 0xff4f66 : b.enabled ? 0x2a3e4a : 0x1a242c);
    b.txt.setColor(b.active ? b.color : b.locked ? '#ff6a6a' : b.enabled ? b.color : '#4a5560');
    if (b.enabled) b.rect.setInteractive({ useHandCursor: true });
    else b.rect.disableInteractive();
  }

  private button(
    x: number,
    y: number,
    w: number,
    h: number,
    label: string,
    enabled: boolean,
    onClick: () => void,
    active = false,
    color = '#c8d4e0',
    kind: ButtonKind = 'priority',
    ref?: string,
    locked = false,
  ): Phaser.GameObjects.Container {
    const rect = this.scene.add
      .rectangle(x, y, w, h, active ? 0x14301c : locked ? 0x301820 : 0x152028, 1)
      .setStrokeStyle(1, active ? 0x7dce5a : locked ? 0xff4f66 : enabled ? 0x2a3e4a : 0x1a242c)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: enabled });
    const txt = this.scene.add
      .text(x + w / 2, y + h / 2, label, {
        fontFamily: 'Consolas, monospace',
        fontSize: '11px',
        color: active ? color : locked ? '#ff6a6a' : enabled ? color : '#4a5560',
      })
      .setOrigin(0.5);

    const b: MenuButton = { rect, txt, enabled, active, kind, ref, color, locked };
    this.buttons.push(b);

    rect.on('pointerover', () => {
      if (b.enabled) rect.setFillStyle(0x1e2c36);
    });
    rect.on('pointerout', () => rect.setFillStyle(b.active ? 0x14301c : b.locked ? 0x301820 : 0x152028));
    rect.on('pointerdown', () => {
      if (b.enabled) onClick();
    });

    return this.scene.add.container(0, 0, [rect, txt]);
  }
}
